import { TeliaCtaLink, TeliaDivider, TeliaSkeleton } from '@teliads/components/react';
import React, { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

import BaseList from '../base-list/BaseList';
import ChipChoice from '../chip-choice/ChipChoice';
import ErrorState from '../error-state/ErrorState';
import { NEWSROOM_CATEGORY_TRANSLATION_KEYS } from '../filters/constants';
import { ArticleCategory, ArticleCategoryExtended } from '../filters/models';

import { urlLanguage } from 'apollo';
import Heading from 'components/voca/heading/Heading';
import { fetchNewsByCategory } from 'services';
import type { TableContent } from 'utils/models';
import './PreviewList.scss';
import { getLinkTarget } from 'utils/utils';

export default function PreviewList({ title, cta }: Omit<TableContent, 'items'>) {
  const { t } = useTranslation();

  const [selectedCategory, setSelectedCategory] = useState<ArticleCategoryExtended>(
    ArticleCategoryExtended.ALL_TOPICS
  );
  const availableCategories = [
    ...Object.keys(ArticleCategoryExtended).map((key) => (ArticleCategoryExtended as any)[key]),
    ...Object.keys(ArticleCategory).map((key) => (ArticleCategory as any)[key]),
  ];

  const currentLanguage = urlLanguage();

  const {
    data: news,
    isError,
    isFetching,
    isFetched,
  } = useQuery(
    [`news-${selectedCategory}`, selectedCategory, currentLanguage],
    () => fetchNewsByCategory(selectedCategory, currentLanguage),
    {
      refetchOnWindowFocus: false,
      keepPreviousData: true,
      retry: false,
    }
  );

  return (
    <section className="preview-list">
      <header className="preview-list-header">
        <Heading tag="h2" variant="title-200" className="preview-list-header__title">
          {title}
        </Heading>

        <div className="preview-list-header__filter">
          {availableCategories.map((category, index) => (
            <React.Fragment key={`${category}-${index}`}>
              <ChipChoice
                checked={category === selectedCategory}
                onClick={() => setSelectedCategory(category)}
                dataTestid="chip-choice"
              >
                {t(NEWSROOM_CATEGORY_TRANSLATION_KEYS[category])}
              </ChipChoice>
            </React.Fragment>
          ))}
        </div>
      </header>

      {isError && <ErrorState errorMessage={t('newsroom_list_error')} type="fetchError" />}

      {news && isFetched && !isError && <BaseList items={news.data.items.slice(0, 5)} />}

      {isFetching && !isFetched && !isError && (
        <ul className="preview-list__skeleton-container">
          <TeliaDivider />

          {Array.from({ length: 5 }).map((_, i) => (
            <Fragment key={i}>
              <TeliaSkeleton
                className="preview-list__skeleton-item"
                dataTestid="preview-list-skeleton"
              />
              <TeliaDivider />
            </Fragment>
          ))}
        </ul>
      )}

      {cta && (
        <TeliaCtaLink
          href={cta.url}
          target={getLinkTarget(cta.url)}
          size="sm"
          variant="expressive"
          className="preview-list__cta-link"
        >
          {cta.text}
        </TeliaCtaLink>
      )}
    </section>
  );
}
